import React, { useState } from 'react';
import { SubmitHandler, FormProvider, useForm } from 'react-hook-form';

import { useGiftCards, useI18n, useLogger, useSelector } from '~Hooks';
import { Box, Button, Divider, Input, SelectableButton } from '~Styles';
import Field from '~ComponentsImproved/RHF/Field';

import { useGiftCardsContext } from './GiftCardsProvider';
import PaymentHandler from './PaymentHandler';
import { useStorage } from '~Components/Storage';

enum DeliveryType {
  Email = 'email',
  Printer = 'printer',
}

type Inputs = {
  deliveryType: DeliveryType;
  amount: number;
  expiryInYears: string;
  email?: string;
};

type TransactionState =
  | { status: 'IDLE' }
  | { status: 'AWAITING_PAYMENT' }
  | { status: 'PENDING' } // Same as loading
  | { status: 'SUCCESS' }
  | { status: 'ERROR'; error: string; errorType: 'payment' | 'gift_card' };

interface CreateGiftCardFormProps {
  onSuccess: (shortcode: string) => void;
  predefinedShortcode?: string;
}

function CreateGiftCardForm({ onSuccess, predefinedShortcode }: CreateGiftCardFormProps) {
  const { i18n } = useI18n();
  const logger = useLogger('create-gift-card-form');
  const giftCardsContext = useGiftCardsContext();
  const giftCard = useGiftCards();
  const [storage] = useStorage();

  const hasPrimaryPrinter = useSelector((state) => state.printers.some((p) => p.primary));

  const [transactionState, setTransactionState] = useState<TransactionState>({ status: 'IDLE' });

  const formMethods = useForm<Inputs>({
    defaultValues: {
      deliveryType: DeliveryType.Email,
      expiryInYears: '1',
    },
    mode: 'onBlur',
  });

  const deliveryType = formMethods.watch('deliveryType');
  const giftCardAmount = formMethods.watch('amount') * 100;
  const giftCardEmail = formMethods.watch('email');
  const giftCardExpiryInYears = formMethods.watch('expiryInYears');

  // Generic error handler, logs error and sets form in error state.
  const handleError = (
    errorMessage?: string,
    errorPayload?: any,
    errorType?: 'payment' | 'gift_card',
  ) => {
    logger.error(errorMessage || 'Create gift card: Unkown error', errorPayload);
    setTransactionState({
      status: 'ERROR',
      error: errorMessage || 'Create gift card: Unkown error',
      errorType: errorType || 'gift_card',
    });
  };

  // Called once the payment has gone through
  const requestCardCreation = () => {
    setTransactionState({ status: 'PENDING' });
    giftCard
      .create({
        emailOnCreate: giftCardEmail,
        initialBalance: giftCardAmount,
        expiryInYears: parseInt(giftCardExpiryInYears) || 1,
        predefinedShortcode,
      })
      .then(({ data: createData, errors: createErrors }) => {
        const createSuccess = createData?.createGiftcard.success;

        if (createSuccess) {
          const shortcode = createData?.createGiftcard.giftcard?.shortcode;

          // Print gift card if delivery type is printer
          if (deliveryType === DeliveryType.Printer) {
            const expiryDate = new Date();
            expiryDate.setFullYear(
              new Date().getFullYear() + parseInt(giftCardExpiryInYears) || 1,
            );

            giftCard.print({
              ...createData.createGiftcard.giftcard,
              expiresAt: createData.createGiftcard.giftcard.expiresAt || expiryDate.toISOString(),
              balance: giftCardAmount,
            });
          }
          setTransactionState({ status: 'SUCCESS' });

          setTimeout(() => {
            onSuccess(shortcode);
          }, 500);
        } else {
          handleError(createErrors?.[0].message || 'Unknown error');
        }
      })
      .catch(handleError);
  };

  const onSubmit: SubmitHandler<Inputs> = () => {
    setTransactionState({ status: 'AWAITING_PAYMENT' });
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = formMethods;

  if (!giftCardsContext.item) {
    return (
      <>
        <h2>{i18n('GC.Error.MissingItem')}</h2>
        <Button
          color="colorWhite"
          backgroundColor="colorBlack"
          onTouchStart={giftCardsContext.refetchItemQueries}
          disabled={giftCardsContext.itemLoading}>
          {i18n('General.Retry')}
        </Button>
      </>
    );
  }

  return (
    <>
      {/* Step 1 - Fill in details */}
      {transactionState.status === 'IDLE' && (
        <>
          <h1 style={{ marginTop: 0 }}>
            {i18n('GC.Create.SellNewGC')} {predefinedShortcode && `(${predefinedShortcode})`}
          </h1>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <h4>{i18n('GC.Create.DeliveryMethod')}</h4>
              <Box marginBottom="large">
                <SelectableButton
                  {...register('deliveryType')}
                  value={DeliveryType.Printer}
                  label={i18n('GC.Create.Print')}
                  disabled={!hasPrimaryPrinter}
                />

                <SelectableButton
                  {...register('deliveryType')}
                  value={DeliveryType.Email}
                  label={i18n('GC.Create.Email')}
                />
              </Box>
              <Divider />

              {deliveryType === DeliveryType.Email && (
                <>
                  <h4>{i18n('GC.Create.EmailRecipient')}</h4>
                  <Field name="email" marginBottom="small">
                    <Input
                      placeholder="example@mail.com"
                      {...register('email', {
                        required: i18n('GC.Create.Email.Required'),
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: i18n('GC.Create.Email.Invalid'),
                        },
                      })}
                      type="email"
                      error={!!errors.email}
                    />
                  </Field>
                </>
              )}
              <Divider />

              <h4>{i18n('GC.Create.Amount')}</h4>
              <Field name="amount" marginBottom="small">
                <Input
                  {...register('amount', {
                    required: i18n('GC.Create.Amount.Required'),
                    min: {
                      value: 5,
                      message: `${i18n('GC.Create.Amount.Minimum')} ${storage.currency.code}`,
                    },
                  })}
                  placeholder={`${i18n('GC.Create.Amount.Placeholder')} ${storage.currency.code}`}
                  type="number"
                  error={!!errors.amount}
                />
              </Field>
              <Divider />

              <h4>{i18n('GC.Create.ExpiryDate')}</h4>
              <SelectableButton
                {...register('expiryInYears')}
                value={'1'}
                label={`1 ${i18n('GC.Create.ExpiryDate.Years')}`}
              />
              <SelectableButton
                {...register('expiryInYears')}
                value={'2'}
                label={`2 ${i18n('GC.Create.ExpiryDate.Years')}`}
              />
              <SelectableButton
                {...register('expiryInYears')}
                value={'3'}
                label={`3 ${i18n('GC.Create.ExpiryDate.Years')}`}
                marginBottom="large"
              />
              <Divider marginBottom="medium" />

              <Box display="flex">
                <Button
                  type="submit"
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  disabled={!isDirty || !isValid || isSubmitting}>
                  {i18n('GC.ProceedToPayment')}
                </Button>
              </Box>
            </form>
          </FormProvider>
        </>
      )}

      {/* Step 2 - Payment */}
      {transactionState.status === 'AWAITING_PAYMENT' && (
        <PaymentHandler
          amount={giftCardAmount}
          onPaymentError={(message, payload) => {
            handleError(message, payload, 'payment');
          }}
          onPaymentSuccess={() => {
            requestCardCreation();
          }}
        />
      )}

      {transactionState.status === 'PENDING' && (
        <Box>
          <p>{i18n('General.Loading')}</p>
        </Box>
      )}

      {/* Step 3 - Success! */}
      {transactionState.status === 'SUCCESS' && (
        <Box>
          <p>{i18n('General.Success')}!</p>
        </Box>
      )}

      {/* Step X - Error */}
      {transactionState.status === 'ERROR' && (
        <Box>
          <p>{i18n('General.Error')}!</p>
          {transactionState.error && transactionState.errorType === 'gift_card' ? (
            <>
              <p>
                The payment went through, but we could not create the card.{' '}
                <b>Use the button below to retry.</b> (No additional payment required).
                <br />
                If this error persists and retrying does not work, please refund the customer and
                contact us.
              </p>
              <Button
                type="button"
                onClick={() => {
                  requestCardCreation();
                }}>
                {i18n('General.Retry')}
              </Button>
            </>
          ) : (
            <p>The payment was not completed, and the customer has not been charged.</p>
          )}
        </Box>
      )}
    </>
  );
}

export default CreateGiftCardForm;
